import { KP } from "./poseHandedness";

/**
 * Pose-based swing (shot) detector.
 *
 * Tracks each player across frames (nearest-centroid association) and watches
 * the dominant-hand wrist speed. A shot is flagged at a local PEAK of wrist
 * speed above a threshold — that's the swing/contact moment. No ball needed.
 *
 * Speed is normalized by frame height so the threshold is resolution-agnostic.
 *
 * ⚠️ Heuristic: detects swings, not exact ball contact; serves/tosses and both
 * players' strokes all produce wrist peaks. Keep it human-confirmed.
 */
export class SwingDetector {
  constructor(opts = {}) {
    this.minSpeed = opts.minSpeed ?? 1.1; // frame-heights / second
    this.debounce = opts.debounce ?? 0.45; // seconds between swings per player
    this.minScore = opts.minScore ?? 0.3; // keypoint confidence
    this.assocRadius = opts.assocRadius ?? 0.18; // frac of width for track match
    this.maxStale = opts.maxStale ?? 12; // frames before dropping a track
    this.reset();
  }

  reset() {
    this.tracks = [];
    this.nextId = 1;
  }

  _centroid(pose) {
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (const k of pose.keypoints) {
      if ((k.score ?? 0) >= this.minScore) {
        sx += k.x;
        sy += k.y;
        n++;
      }
    }
    return n ? { x: sx / n, y: sy / n } : null;
  }

  /**
   * @param poses MoveNet poses
   * @param t seconds
   * @param w,h frame dims (px)
   * @param dominant "right" | "left"
   * @returns array of swing events { t, x, y, player, speed, handed, pose }
   */
  update(poses, t, w, h, dominant = "right") {
    const events = [];
    const used = new Set();

    // associate existing tracks to nearest unused pose
    for (const tr of this.tracks) {
      let best = -1;
      let bestD = Infinity;
      for (let i = 0; i < poses.length; i++) {
        if (used.has(i)) continue;
        const c = this._centroid(poses[i]);
        if (!c) continue;
        const d = Math.hypot(c.x - tr.cx, c.y - tr.cy);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      if (best >= 0 && bestD < this.assocRadius * w) {
        used.add(best);
        this._advance(tr, poses[best], t, h, dominant, events);
      } else {
        tr.stale = (tr.stale || 0) + 1;
      }
    }

    // spawn tracks for unmatched poses
    for (let i = 0; i < poses.length; i++) {
      if (used.has(i)) continue;
      const c = this._centroid(poses[i]);
      if (!c) continue;
      const tr = {
        id: this.nextId++,
        cx: c.x,
        cy: c.y,
        prevWrist: null,
        prevT: null,
        sp0: 0,
        sp1: 0,
        lastSwingT: -999,
        stale: 0,
      };
      this.tracks.push(tr);
      this._advance(tr, poses[i], t, h, dominant, events);
    }

    this.tracks = this.tracks.filter((tr) => (tr.stale || 0) < this.maxStale);
    return events;
  }

  _advance(tr, pose, t, h, dominant, events) {
    const c = this._centroid(pose);
    if (c) {
      tr.cx = c.x;
      tr.cy = c.y;
    }
    tr.stale = 0;

    const wi = dominant === "left" ? KP.leftWrist : KP.rightWrist;
    const wr = pose.keypoints[wi];
    if (!wr || (wr.score ?? 0) < this.minScore) {
      tr.prevWrist = null;
      return;
    }

    let speed = 0;
    if (tr.prevWrist && tr.prevT != null) {
      const dt = Math.max(1 / 60, t - tr.prevT);
      const dist = Math.hypot(wr.x - tr.prevWrist.x, wr.y - tr.prevWrist.y);
      speed = dist / h / dt; // frame-heights per second
    }
    tr.prevWrist = { x: wr.x, y: wr.y };
    tr.prevT = t;

    // local-peak detection over [sp0, sp1, speed]
    const sp0 = tr.sp0;
    const sp1 = tr.sp1;
    if (
      sp1 > this.minSpeed &&
      sp1 >= sp0 &&
      sp1 > speed &&
      t - tr.lastSwingT > this.debounce
    ) {
      tr.lastSwingT = t;
      events.push({
        t,
        x: wr.x,
        y: wr.y,
        player: tr.id,
        speed: sp1,
        handed: this._classify(pose, wr, dominant),
        pose,
      });
    }
    tr.sp0 = sp1;
    tr.sp1 = speed;
  }

  _classify(pose, wrist, dominant) {
    const ls = pose.keypoints[KP.leftShoulder];
    const rs = pose.keypoints[KP.rightShoulder];
    if (!ls || !rs) return null;
    const cx = (ls.x + rs.x) / 2;
    const cy = (ls.y + rs.y) / 2;
    const svx = rs.x - ls.x;
    const svy = rs.y - ls.y;
    const proj = (wrist.x - cx) * svx + (wrist.y - cy) * svy;
    const side = proj >= 0 ? "right" : "left";
    const type = side === dominant ? "forehand" : "backhand";
    return { type, side };
  }
}
